import { useI18nStore } from '@/i18n';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { AI_VOICE_MAX_SECONDS, AI_VOICE_SAMPLE_RATE } from '@restaurant/api-contracts';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createVoiceSession, isRefusal, transcribeRecording } from '../api/voice-api';
import { openLiveTranscription, type LiveTranscription } from '../lib/live-transcription';
import { startPcmCapture, type PcmCapture } from '../lib/pcm-capture';

export type VoiceStatus = 'idle' | 'starting' | 'listening' | 'transcribing';

export type VoiceProblem = 'permission-denied' | 'no-microphone' | 'unsupported' | 'failed';

export interface VoiceError {
  problem: VoiceProblem;
  detail?: string;
}

const RECORDER_TYPES = [
  'audio/webm;codecs=opus',
  'audio/mp4',
  'audio/webm',
  'audio/ogg;codecs=opus',
];

interface Run {
  phase: 'recording' | 'stopping' | 'done';
  stream: MediaStream;
  context: AudioContext | null;
  capture: PcmCapture | null;
  live: LiveTranscription | null;
  recorder: MediaRecorder | null;
  chunks: Blob[];
  startedAt: number;
  stopTimer: ReturnType<typeof setTimeout> | undefined;
}

export function microphoneProblem(error: unknown): VoiceProblem {
  const name = error instanceof Error ? error.name : '';
  if (name === 'NotAllowedError' || name === 'SecurityError') return 'permission-denied';
  if (name === 'NotFoundError' || name === 'OverconstrainedError') return 'no-microphone';
  return 'failed';
}

function createRecorder(stream: MediaStream, chunks: Blob[]): MediaRecorder | null {
  if (typeof MediaRecorder === 'undefined') return null;
  const mimeType = RECORDER_TYPES.find((type) => MediaRecorder.isTypeSupported(type));
  try {
    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    recorder.addEventListener('dataavailable', (event) => {
      if (event.data.size > 0) chunks.push(event.data);
    });
    recorder.start();
    return recorder;
  } catch {
    return null;
  }
}

function finishRecording(run: Run): Promise<Blob> {
  const { recorder, chunks } = run;
  if (!recorder) return Promise.reject(new Error('Recording is unavailable'));
  const collect = () => new Blob(chunks, { type: recorder.mimeType });
  if (recorder.state === 'inactive') return Promise.resolve(collect());
  return new Promise((resolve) => {
    recorder.addEventListener('stop', () => resolve(collect()), { once: true });
    recorder.stop();
  });
}

function teardown(run: Run) {
  run.phase = 'done';
  clearTimeout(run.stopTimer);
  run.live?.close();
  run.capture?.stop();
  if (run.recorder && run.recorder.state !== 'inactive') run.recorder.stop();
  run.stream.getTracks().forEach((track) => track.stop());
  void run.context?.close();
}

export function useVoiceTranscription(onTranscript: (text: string) => void) {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const locale = useI18nStore((s) => s.locale);
  const [status, setStatus] = useState<VoiceStatus>('idle');
  const [live, setLive] = useState(false);
  const [level, setLevel] = useState(0);
  const [error, setError] = useState<VoiceError | null>(null);
  const runRef = useRef<Run | null>(null);
  const startingRef = useRef(false);
  const mountedRef = useRef(true);
  const latest = useRef({ onTranscript, restaurantId, locale });
  latest.current = { onTranscript, restaurantId, locale };

  const end = useCallback((run: Run, failure: VoiceError | null) => {
    teardown(run);
    if (runRef.current !== run) return;
    runRef.current = null;
    setStatus('idle');
    setLive(false);
    setLevel(0);
    setError(failure);
  }, []);

  const degrade = useCallback(
    (run: Run) => {
      run.live?.close();
      run.live = null;
      if (runRef.current === run) setLive(false);
      if (!run.recorder && run.phase === 'recording') end(run, { problem: 'failed' });
    },
    [end],
  );

  const connectLive = useCallback(
    async (run: Run) => {
      const queued: ArrayBuffer[] = [];
      try {
        if (!run.context) throw new Error('Web Audio is unavailable');
        await run.context.resume();
        run.capture = await startPcmCapture({
          context: run.context,
          stream: run.stream,
          sampleRate: AI_VOICE_SAMPLE_RATE,
          onChunk: (pcm) => (run.live ? run.live.send(pcm) : queued.push(pcm)),
          onLevel: (value) => {
            if (runRef.current === run) setLevel(value);
          },
        });
        if (run.phase !== 'recording') return;
        const session = await createVoiceSession(
          latest.current.restaurantId,
          latest.current.locale,
        );
        if (run.phase !== 'recording') return;
        const transcription = openLiveTranscription(session, {
          onText: (text) => {
            if (run.phase === 'recording') latest.current.onTranscript(text);
          },
          onFailure: () => degrade(run),
        });
        queued.splice(0).forEach((pcm) => transcription.send(pcm));
        run.live = transcription;
        setLive(true);
      } catch (failure) {
        if (isRefusal(failure)) {
          end(run, { problem: 'failed', detail: failure.message });
          return;
        }
        degrade(run);
      }
    },
    [degrade, end],
  );

  const stop = useCallback(async () => {
    const run = runRef.current;
    if (!run || run.phase !== 'recording') return;
    run.phase = 'stopping';
    clearTimeout(run.stopTimer);
    setStatus('transcribing');
    const durationMs = performance.now() - run.startedAt;
    try {
      let text: string | null = null;
      const { live: transcription, capture } = run;
      if (transcription && capture) {
        await capture.flush();
        text = await transcription.finish().catch(() => null);
      }
      if (text === null) {
        const audio = await finishRecording(run);
        if (audio.size === 0) throw new Error('Nothing was recorded');
        text = await transcribeRecording({
          restaurantId: latest.current.restaurantId,
          audio,
          durationMs,
          locale: latest.current.locale,
        });
      }
      if (runRef.current === run) latest.current.onTranscript(text);
      end(run, null);
    } catch (failure) {
      end(run, {
        problem: 'failed',
        detail: isRefusal(failure) ? failure.message : undefined,
      });
    }
  }, [end]);

  const start = useCallback(async () => {
    if (runRef.current || startingRef.current) return;
    setError(null);
    if (!navigator.mediaDevices?.getUserMedia) {
      setError({ problem: 'unsupported' });
      return;
    }
    startingRef.current = true;
    setStatus('starting');
    const context = typeof AudioContext === 'function' ? new AudioContext() : null;
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, channelCount: 1 },
      });
    } catch (failure) {
      startingRef.current = false;
      void context?.close();
      setStatus('idle');
      setError({ problem: microphoneProblem(failure) });
      return;
    }
    startingRef.current = false;
    if (!mountedRef.current) {
      stream.getTracks().forEach((track) => track.stop());
      void context?.close();
      return;
    }
    const chunks: Blob[] = [];
    const run: Run = {
      phase: 'recording',
      stream,
      context,
      capture: null,
      live: null,
      recorder: createRecorder(stream, chunks),
      chunks,
      startedAt: performance.now(),
      stopTimer: undefined,
    };
    runRef.current = run;
    run.stopTimer = setTimeout(() => void stop(), AI_VOICE_MAX_SECONDS * 1000);
    setStatus('listening');
    void connectLive(run);
  }, [connectLive, stop]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (runRef.current) teardown(runRef.current);
      runRef.current = null;
    };
  }, []);

  return {
    status,
    live,
    level,
    error,
    available: restaurantId !== '',
    start,
    stop,
  };
}
